import { useState, useEffect } from 'react';
import { useSchedule } from '../useSchedule';
import { filterScheduleForStage, classifyEntries } from '../utils';
import { nextMarketDay } from '../../../utils/marketStatus';
import { captureEvent } from '../../../observability';
import { ExternalLinkIcon } from '../../../components/primitives/Icon';
import styles from './StageSchedule.module.css';

interface Props {
  /**
   * Location name of the stage — matched against the schedule sheet's
   * `Stage` column. The market layout has one stage per street, so the
   * position's resolved location name (via `nameFor()`) is the join key.
   */
  stageLocation: string;
  /** Override "now" for testing — disables the auto-refresh tick. */
  now?: Date;
}

export function StageSchedule({ stageLocation, now }: Props) {
  const { entries, isLoading } = useSchedule();

  // Live clock: re-evaluate now/next badges every minute while open.
  // Disabled when `now` is injected so tests use a fixed timestamp.
  const [liveNow, setLiveNow] = useState(() => new Date());
  useEffect(() => {
    if (now !== undefined) return;
    const id = setInterval(() => setLiveNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [now]);

  const today = now ?? liveNow;
  const marketDay = nextMarketDay(today);
  const stageEntries = filterScheduleForStage(entries, stageLocation, marketDay);
  const statuses = classifyEntries(stageEntries, today, marketDay);

  useEffect(() => {
    if (stageEntries.length === 0) return;
    // Snapshot at the moment entries first resolve — intentionally does not
    // re-fire on clock ticks so the event isn't spammed every 60 seconds.
    const nowAtLoad = statuses.includes('now');
    captureEvent('stage_lineup_viewed', { stage_name: stageLocation, has_now_performer: nowAtLoad });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageLocation, stageEntries.length > 0]);

  const fullScheduleLink = (
    <a
      href="https://www.troymarket.org/music"
      target="_blank"
      rel="noopener noreferrer"
      className={styles.fullScheduleBtn}
    >
      <span>View full schedule</span>
      <ExternalLinkIcon size={13} />
    </a>
  );

  const headingLabel = `This Week's Lineup · ${marketDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  if (isLoading) {
    return (
      <div className={styles.section}>
        <p className={styles.heading}>{headingLabel}</p>
        <p className={styles.loading}>Loading schedule…</p>
        {fullScheduleLink}
      </div>
    );
  }

  if (stageEntries.length === 0) {
    return (
      <div className={styles.section}>
        {fullScheduleLink}
      </div>
    );
  }

  return (
    <section className={styles.section} aria-label="Stage schedule">
      <p className={styles.heading}>{headingLabel}</p>
      <ul className={styles.list}>
        {stageEntries.map((entry, i) => {
          const status = statuses[i];
          const isNow = status === 'now';
          const isNext = status === 'next';
          const isPast = status === 'past';

          const timeLabel = entry.endTime
            ? `${entry.startTime}–${entry.endTime}`
            : entry.startTime;

          const rowClass = [
            styles.row,
            isNow && styles.rowNow,
            isNext && styles.rowNext,
            isPast && styles.rowPast,
          ].filter(Boolean).join(' ');

          return (
            <li key={`${entry.startTime}-${entry.musician}`} className={rowClass}>
              <div className={styles.meta}>
                <span className={styles.time}>{timeLabel}</span>
                {isNow && (
                  <span className={`${styles.badge} ${styles.badgeNow}`} aria-label="Currently playing">
                    Now
                  </span>
                )}
                {isNext && (
                  <span className={`${styles.badge} ${styles.badgeNext}`} aria-label="Up next">
                    Next
                  </span>
                )}
              </div>
              <span className={styles.musician}>{entry.musician}</span>
            </li>
          );
        })}
      </ul>
      {fullScheduleLink}
    </section>
  );
}
